import { useMemo } from 'react';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { EntryConfirmationRow } from '@/components/checkout/EntryConfirmation';
import { getEntryTitle } from '@/feature/entries-pickem/entries-utils';
import { toLocaleCurrency } from '@/utils/numeric/currency';

type UseEntryRowsParams = {
    entry: EntryInfoFragment | undefined;
    isMysteryMode: boolean;
    isFreeEntry: boolean;
    isRaceToPlaceEntry: boolean;
    picksCount: number;
    multiplier: string;
};

type EntryRowsInfo = {
    title: string;
    rows: EntryConfirmationRow[];
};

/** Builds the title and summary rows (picks, multiplier, entry amount, payout) shown on the entry confirmation screen. */
export const useEntryRows = ({
    entry,
    isMysteryMode,
    isFreeEntry,
    isRaceToPlaceEntry,
    picksCount,
    multiplier,
}: UseEntryRowsParams): EntryRowsInfo => {
    const multiplierValue = isMysteryMode
        ? entry?.initialMultiplier
            ? `${entry.initialMultiplier}x`
            : ''
        : multiplier;

    const gameModeTitle = getEntryTitle(entry?.gameMode);
    const title = isRaceToPlaceEntry ? 'Perfect entry placed' : gameModeTitle ? `${gameModeTitle} entry placed!` : '';

    const rows = useMemo<EntryConfirmationRow[]>(
        () => [
            { label: 'Picks', value: picksCount.toString() },
            // free entries don't show the multiplier
            ...(isFreeEntry
                ? []
                : [{ label: isMysteryMode ? 'Mystery multiplier' : 'Multiplier', value: multiplierValue }]),
            {
                label: isFreeEntry ? 'Free entry' : 'Entry amount',
                value: entry?.amount ? toLocaleCurrency(entry.amount) : '',
            },
            { label: 'To win', value: entry?.toWin ? toLocaleCurrency(entry.toWin) : '', emphasized: true },
        ],
        [picksCount, isFreeEntry, isMysteryMode, multiplierValue, entry?.amount, entry?.toWin]
    );

    return { title, rows };
};
